import { Map } from 'immutable';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Selector } from 'reselect';

import { dispatch } from '../../dispatch';
import { IEffect, IPayloadlessEffect, ISomeEffect, IStatefulEffect, IStatefulPayloadlessEffect } from '../../types';

import { QUEUE_EFFECT } from './constants';

export interface IComposeEffectMapCallbackParams {
  effect<EP, ER>(run: (eff: EP) => Promise<ER>): IEffect<string, EP, ER>;

  payloadlessEffect<ER>(run: () => Promise<ER>): IPayloadlessEffect<string, ER>;

  statefulEffect<EP, ER, SR>(
    select: (state: Map<string, any>) => SR,
    run: (state: SR, eff: EP) => Promise<ER>
  ): IStatefulEffect<string, EP, ER, SR>;

  statefulPayloadlessEffect<ER, SR>(
    select: (state: Map<string, any>) => SR,
    run: (state: SR) => Promise<ER>
  ): IStatefulPayloadlessEffect<string, ER, SR>;
}

export function effect<EP, ER, SR>(
  effectType: string,
  run: (state: SR, eff: EP) => Promise<ER>,
  effectKind: ISomeEffect['effectKind'],
  selector?: Selector<Map<string, any>, SR>
): ISomeEffect {
  return Object.assign(
    async (effectPayload: EP) =>
      new Promise<ER>((resolve, reject) => {
        // indirection is here to avoid circular dependencies within the framework
        dispatch({ type: QUEUE_EFFECT, payload: { effectType, effect: effectPayload, resolve, reject } });
      }),
    {
      effectType,
      effectKind,
      run,
      selector,
      /**
       * A React effect Hook that runs your effect when its parameters change.
       * It uses deep equality but if your effect changes the parameters that
       * are passed into the same effect, you may get stuck in an infinite loop.
       */
      use: (effectPayload: EP) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const previousInput = useRef<EP>();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [state, setState] = useState<{ loading: boolean; result?: ER; error?: Error }>({
          loading: false,
          result: null,
          error: null
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (isEqual(previousInput.current, effectPayload)) {
            // If the payload was previously received, don't execute the effect and
            // the promise created above is never resolved or rejected so handlers aren't called
            return;
          }

          previousInput.current = effectPayload;

          let resolve;
          let reject;
          const promise = new Promise<ER>((res, rej) => {
            resolve = res;
            reject = rej;
          });

          promise
            .then((result) => {
              setState({ loading: false, result, error: null });
            })
            .catch((error) => {
              setState({ loading: false, result: null, error });
            });
          dispatch({ type: QUEUE_EFFECT, payload: { effectType, effect: effectPayload, resolve, reject } });
        });

        return state;
      }
    }
  ) as ISomeEffect;
}
